import {
  authFetcher,
  CHAT_PEEK_URL,
  CHAT_UNREAD_URL,
  CHAT_URL,
  fetchWithAuth,
} from "./const.routes.ts";
import useSWR, { mutate } from "swr";
import { fetchHandler } from "./fetch";
import { ChatStatusType, ChatType } from "../types/Chat.ts";
import { Result } from "../types/Result.ts";

export function useChats(fallback: string[]) {
  const { data, error, isLoading } = useSWR<string[], Error>(
    CHAT_URL + "owner",
    authFetcher,
  );
  return {
    chatsId: data ? data : fallback,
    isLoading,
    isError: error,
  };
}

export function useChat(id: string, fallback: ChatType) {
  const { data, error, isLoading } = useSWR<ChatType, Error>(
    `${CHAT_URL}${id}`,
    authFetcher,
  );

  return {
    chat: data ? data : fallback,
    isLoading,
    isError: error,
  };
}

export function useExistingChat(itemId: string) {
  const { data, error, isLoading } = useSWR<ChatType | "NO CHAT", Error>(
    `${CHAT_PEEK_URL}${itemId}`,
    authFetcher,
  );
  return {
    chat: data ? data : null,
    isLoading,
    isError: error,
  };
}

export function useUnread(fallback: number) {
  const { data, error, isLoading } = useSWR<number, Error>(
    `${CHAT_UNREAD_URL}`,
    authFetcher,
    { keepPreviousData: true, refreshInterval: 30_000 },
  );
  return {
    chat: data ? data : fallback,
    isLoading,
    isError: error,
  };
}

export async function postChat(
  productId: string,
): Promise<Result<ChatType, string>> {
  return await fetchHandler(
    fetchWithAuth(CHAT_URL + productId, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    }),
  );
}

export async function setChatState(chatId: string, state: ChatStatusType) {
  const url = CHAT_URL + `state/${chatId}`;

  return await fetchHandler<ChatType>(
    fetchWithAuth(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        state: state,
      }),
    }),
  );
}

export async function deleteChat(chatId: string) {
  const url = CHAT_URL + `${chatId}`;
  const response = await fetchWithAuth(url, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });
  if (response.ok) {
    await mutate(CHAT_URL + "owner");
    mutate(url);
    const data = (await response.json()) as ChatType;
    return { ok: true, value: data };
  } else {
    return { ok: false, error: response.statusText };
  }
}
